import { Vector2 } from 'three';

import type { ExportManager } from '../export-manager.class';
import type { ImageRenderOptions } from '../types';

/**
 * Render the current scene to an image data URL
 */
export const getImageRenderCfn = (
	self: ExportManager,
	options: ImageRenderOptions = {}
): string | null => {
	const { renderer, scene, camera } = self.configAccess;
	const { pixelRatio = 1, format = 'png', quality = 0.92, width, height } = options;

	if (!renderer || !scene || !camera) {
		console.warn('ExportManager.getImageRender: Missing renderer, scene, or camera');
		return null;
	}

	// Store originals to restore after capture
	const originalPixelRatio = renderer.getPixelRatio();
	const originalSize = renderer.getSize(new Vector2());
	const originalAspect = camera.aspect;

	try {
		// Set the desired pixel ratio for high-res render
		renderer.setPixelRatio(pixelRatio);

		// Temporarily resize renderer and camera if width/height provided
		if (width && height) {
			renderer.setSize(width, height);
			camera.aspect = width / height;
			camera.updateProjectionMatrix();
		}

		// Render the scene
		renderer.render(scene, camera);

		// Get the data URL with specified format
		const mimeType = `image/${format}`;
		const dataUrl = renderer.domElement.toDataURL(mimeType, quality);

		return dataUrl;
	} finally {
		// Always restore originals
		renderer.setPixelRatio(originalPixelRatio);
		if (width && height) {
			renderer.setSize(originalSize.x, originalSize.y);
			camera.aspect = originalAspect;
			camera.updateProjectionMatrix();
		}
		// setSize/setPixelRatio clear the drawing buffer; with the demand render loop idle the canvas would stay blank
		renderer.render(scene, camera);
	}
};
